import { ProductSubTier } from '@sparemin/api-sdk';
import * as React from 'react';

import {
  ApiSubscription,
  CancelledSubscription,
  EnterpriseSubscription,
  FreeSubscription,
  ProEducationSubscription,
  ProSubscription,
  ProTrialSubscription,
} from 'blocks/Pricing';
import BasicSubscription from 'blocks/Pricing/containers/BasicSubscription/BasicSubscription';
import EducationSubscription from 'blocks/Pricing/containers/EducationSubscription';
import ProGrowSubscription from 'blocks/Pricing/containers/ProGrowSubscription';
import UnlimitedSubscription from 'blocks/Pricing/containers/UnlimitedSubscription';
import LoadingOverlay from 'components/LoadingOverlay';
import { PRO_TIERS } from 'redux/middleware/api/plan-service';
import bem from 'utils/bem';
import SettingsContent from '../SettingsContent';

export interface SubscriptionSettingsProps {
  isLoading?: boolean;
  pricingTier?: ProductSubTier;
  autoRenewal?: boolean;
}

const block = bem('subscription-settings');

/*
 * NB: subscription data is loaded when settings page mounts, that way we don't
 *  query the api every time the user switches settings tabs. this also avoids
 *  the "if we have this data don't query for it" optimization which can lead to
 *  stale data
 */

const SubscriptionSettings: React.SFC<SubscriptionSettingsProps> = ({
  isLoading,
  pricingTier,
  autoRenewal,
}) => {
  if (isLoading) {
    return (
      <LoadingOverlay
        className={block('loading-overlay')}
        title="Loading Plan"
      />
    );
  }

  const element = (() => {
    if (
      pricingTier !== ProductSubTier.FREE &&
      pricingTier !== ProductSubTier.PRO_TRIAL &&
      autoRenewal === false
    ) {
      return <CancelledSubscription />;
    }

    switch (pricingTier) {
      case ProductSubTier.FREE:
        return <FreeSubscription />;

      case ProductSubTier.BASIC_20200922_PAID:
      case ProductSubTier.BASIC_20211112_PAID:
      case ProductSubTier.BASIC_20250516_PAID:
      case ProductSubTier.BASIC_20250818_PAID:
        return <BasicSubscription />;

      case ProductSubTier.PRO_TRIAL:
        return <ProTrialSubscription />;

      // TODO: SPAR_15945 really need an easier way than adding new enums everywhere...
      case ProductSubTier.PRO_PAID:
      case ProductSubTier.PRO_20200922_PAID:
      case ProductSubTier.PRO_20211112_PAID:
      case ProductSubTier.PRO_20250516_PAID:
      case ProductSubTier.PRO_20250818_PAID:
        return <ProSubscription />;

      case ProductSubTier.PRO_GROW_20261120_PAID:
        return <ProGrowSubscription />;

      case ProductSubTier.PRO_EDUCATION_CHARITY:
      case ProductSubTier.PRO_ADMIN_ASSIGNED:
      case ProductSubTier.PRO_FREE_TEMPORARY:
        return <ProEducationSubscription />;

      case ProductSubTier.UNLIMITED_20211112_PAID:
      case ProductSubTier.UNLIMITED_ADMIN_ASSIGNED:
        return <UnlimitedSubscription />;

      case ProductSubTier.ENTERPRISE:
        return <EnterpriseSubscription />;

      case ProductSubTier.ENTERPRISE_API_INTEGRATOR:
        return <ApiSubscription />;

      case ProductSubTier.EDUCATION_NONPROFIT:
        return <EducationSubscription />;

      case ProductSubTier.PRO_PAUSED:
        return <ProSubscription />;

      case ProductSubTier.BASIC_PAUSED:
        return <BasicSubscription />;
    }

    return null;
  })();

  const fullBleed =
    [...PRO_TIERS, ProductSubTier.EDUCATION_NONPROFIT].includes(pricingTier) &&
    autoRenewal !== false;

  return (
    <SettingsContent
      bodyClassName={block('body')}
      className={block({ free: !fullBleed })}
      fullBleed={fullBleed}
    >
      {element}
    </SettingsContent>
  );
};

export default SubscriptionSettings;
